<template>
  <PageHeader title="Campaign Reporting" :breadcrumbs="[{ label: 'Beta' }, { label: 'Outreach' }]"  />

  <div class="p-4">

    <!-- Campaign Selector -->
    <div class="mb-4">
      <label for="campaign-select" class="block text-xs font-medium text-gray-700 mb-1">
        Select Campaign
      </label>
      <select
        id="campaign-select"
        v-model="selectedCampaignId"
        @change="fetchReportingData"
        class="w-full md:w-96 px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-dark focus:border-blue-500"
      >
        <option :value="null">-- Select a campaign --</option>
        <option v-for="campaign in campaigns" :key="campaign.id" :value="campaign.id">
          {{ campaign.name }} ({{ campaign.status }})
        </option>
      </select>
    </div>

    <!-- Loading State -->
    <LoadingIndicator v-if="loading" message="Loading report data..." />

    <!-- Reporting Dashboard -->
    <div v-else-if="reportingData && selectedCampaignId" class="space-y-4">
      <!-- Campaign Header -->
      <div class="bg-white shadow rounded-lg">
        <ComponentHeader :title="reportingData.campaign_name" />
        <div class="px-4 pb-4">
          <div class="flex flex-wrap gap-3 text-xs text-gray-600">
            <div>
              <span class="font-medium">Holdback:</span>
              {{
                reportingData.holdback_enabled
                  ? `Enabled (${reportingData.holdback_config?.percentage || 10}%)`
                  : 'Disabled'
              }}
            </div>
            <div>
              <span class="font-medium">Appointment Type:</span>
              {{ reportingData.appointment_type }}
            </div>
          </div>
        </div>
      </div>

      <!-- Overall Metrics -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="bg-white shadow rounded-lg p-4">
          <div class="text-xs font-medium text-gray-500 uppercase">Total Tasks</div>
          <div class="mt-1 text-2xl font-semibold text-gray-900">{{ reportingData.total_tasks }}</div>
        </div>
        <div class="bg-white shadow rounded-lg p-4">
          <div class="text-xs font-medium text-gray-500 uppercase">Total Calls Made</div>
          <div class="mt-1 text-2xl font-semibold text-blue-600">{{ reportingData.total_calls_made }}</div>
        </div>
        <div class="bg-white shadow rounded-lg p-4">
          <div class="text-xs font-medium text-gray-500 uppercase">Calls with Appointments</div>
          <div class="mt-1 text-2xl font-semibold text-green-600">{{ reportingData.calls_with_appointments }}</div>
        </div>
      </div>

      <!-- Calls Chart -->
      <div class="bg-white shadow rounded-lg">
        <ComponentHeader title="Call Outcomes" :heading-level="3" />
        <div class="p-4">
          <ChartV4 :chart_data="callsChartData" :chart_config="callsChartConfig" />
        </div>
      </div>

      <!-- Called Cohort Metrics -->
      <div class="bg-white shadow rounded-lg">
        <ComponentHeader title="Called Cohort" :heading-level="3" />
        <div class="p-4">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <div class="text-xs font-medium text-gray-500">Cohort Size</div>
              <div class="mt-1 text-xl font-semibold text-gray-900">{{ reportingData.called_cohort.size }}</div>
            </div>
            <div>
              <div class="text-xs font-medium text-gray-500">Calls Made</div>
              <div class="mt-1 text-xl font-semibold text-blue-600">{{ reportingData.called_cohort.calls_made }}</div>
            </div>
            <div>
              <div class="text-xs font-medium text-gray-500">Appointments Scheduled</div>
              <div class="mt-1 text-xl font-semibold text-green-600">
                {{ reportingData.called_cohort.appointments_scheduled }}
              </div>
            </div>
            <div>
              <div class="text-xs font-medium text-gray-500">Appointment Rate</div>
              <div class="mt-1 text-xl font-semibold text-green-600">
                {{ reportingData.called_cohort.appointment_rate }}%
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Holdback Cohort Metrics (only if enabled) -->
      <div v-if="reportingData.holdback_enabled && reportingData.holdback_cohort" class="bg-white shadow rounded-lg">
        <ComponentHeader title="Holdback Cohort (Control Group)" :heading-level="3" />
        <div class="p-4">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <div class="text-xs font-medium text-gray-500">Cohort Size</div>
              <div class="mt-1 text-xl font-semibold text-gray-900">{{ reportingData.holdback_cohort.size }}</div>
            </div>
            <div>
              <div class="text-xs font-medium text-gray-500">Appointments Scheduled</div>
              <div class="mt-1 text-xl font-semibold text-green-600">
                {{ reportingData.holdback_cohort.appointments_scheduled }}
              </div>
            </div>
            <div>
              <div class="text-xs font-medium text-gray-500">Appointment Rate</div>
              <div class="mt-1 text-xl font-semibold text-green-600">
                {{ reportingData.holdback_cohort.appointment_rate }}%
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Lift Metric (only if holdback enabled) -->
      <div
        v-if="reportingData.holdback_enabled && reportingData.lift"
        class="bg-primary-50 shadow rounded-lg border-2"
        :class="getLiftBorderColor()"
      >
        <ComponentHeader title="Campaign Lift Analysis" :heading-level="3" />
        <div class="p-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div class="text-xs font-medium text-gray-600 mb-1.5">Lift Percentage</div>
              <div class="text-3xl font-bold" :class="getLiftTextColor()">
                <span v-if="reportingData.lift.is_infinite">∞</span>
                <span v-else-if="reportingData.lift.percentage !== null">
                  {{ reportingData.lift.percentage > 0 ? '+' : '' }}{{ reportingData.lift.percentage }}%
                </span>
                <span v-else>N/A</span>
              </div>
            </div>
            <div>
              <div class="text-xs font-medium text-gray-600 mb-1.5">Interpretation</div>
              <div class="text-sm font-medium text-gray-800">
                {{ reportingData.lift.interpretation || 'No data available' }}
              </div>
              <div v-if="reportingData.lift.percentage !== null" class="mt-2 text-xs text-gray-600">
                The campaign performed
                <span class="font-semibold">
                  {{ Math.abs(reportingData.lift.percentage) }}%
                  {{ reportingData.lift.percentage > 0 ? 'better' : 'worse' }}
                </span>
                than the control group.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Explanation Card -->
      <div class="bg-blue-50 border-l-4 border-blue-400 p-3">
        <div class="flex">
          <div class="shrink-0">
            <svg class="h-4 w-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-2">
            <p class="text-xs text-blue-700">
              <strong>About Lift:</strong> Lift measures the effectiveness of the campaign by comparing the appointment
              rate of the called cohort against the holdback (control) cohort. A positive lift indicates the campaign is
              driving more appointments than would occur naturally.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- No Campaign Selected -->
    <div v-else-if="!selectedCampaignId" class="text-center py-8 text-gray-500">
      <svg class="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
      <p class="mt-3 text-sm">Select a campaign to view reporting metrics</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'
import ChartV4 from '@/components/widgetcomponents/Viz_Elements/Chart_V4.vue'
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue'
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue'
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue'

export default {
  name: 'ReportingTab',
  components: {
    ChartV4,
    PageHeader,
    ComponentHeader,
    LoadingIndicator,
  },
  data() {
    return {
      campaigns: [],
      selectedCampaignId: null,
      reportingData: null,
      loading: false,
      toast: useToast(),
    }
  },
  computed: {
    callsChartData() {
      if (!this.reportingData || !this.reportingData.daily_calls) return null

      const dailyData = this.reportingData.daily_calls

      // Extract dates and values by call outcome
      const labels = dailyData.map(d => d.date)
      const appointmentScheduled = dailyData.map(d => d.appointment_scheduled || 0)
      const callbackRequested = dailyData.map(d => d.callback_requested || 0)
      const declined = dailyData.map(d => d.declined || 0)
      const leftVoicemail = dailyData.map(d => d.left_voicemail || 0)
      const wrongNumber = dailyData.map(d => d.wrong_number || 0)
      const noAnswer = dailyData.map(d => d.no_answer || 0)
      const busy = dailyData.map(d => d.busy || 0)
      const failed = dailyData.map(d => d.failed || 0)
      const other = dailyData.map(d => d.other || 0)

      return {
        labels: labels,
        series: [
          {
            values: appointmentScheduled,
            config: {
              label: 'Appointment Scheduled',
              backgroundColor: 'rgba(34, 197, 94, 0.9)', // Green
              type: 'bar',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: callbackRequested,
            config: {
              label: 'Callback Requested',
              backgroundColor: 'rgba(59, 130, 246, 0.9)', // Blue
              type: 'bar',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: leftVoicemail,
            config: {
              label: 'Left Voicemail',
              backgroundColor: 'rgba(147, 197, 253, 0.9)', // Light Blue
              type: 'bar',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: declined,
            config: {
              label: 'Declined',
              backgroundColor: 'rgba(239, 68, 68, 0.9)', // Red
              type: 'bar',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: wrongNumber,
            config: {
              label: 'Wrong Number',
              backgroundColor: 'rgba(251, 146, 60, 0.9)', // Orange
              type: 'bar',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: noAnswer,
            config: {
              label: 'No Answer',
              backgroundColor: 'rgba(156, 163, 175, 0.9)', // Gray
              type: 'bar',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: busy,
            config: {
              label: 'Busy',
              backgroundColor: 'rgba(209, 213, 219, 0.9)', // Light Gray
              type: 'bar',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: failed,
            config: {
              label: 'Failed',
              backgroundColor: 'rgba(127, 29, 29, 0.9)', // Dark Red
              type: 'bar',
              yAxis: 'left',
              offset: 0,
            },
          },
          {
            values: other,
            config: {
              label: 'Other',
              backgroundColor: 'rgba(251, 191, 36, 0.9)', // Amber
              type: 'bar',
              yAxis: 'left',
              offset: 0,
            },
          },
        ],
      }
    },
    callsChartConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 40, right: 30, bottom: 60, left: 60 },
        maxBarWidth: 50,
        xAxis: {
          labelRotation: -45,
          tickFormat: null,
          fontSize: 9,
        },
        yAxes: [
          {
            position: 'left',
            tickFormat: (d) => d.toFixed(0),
            fontSize: 10,
          },
        ],
        legend: {
          display: true,
          position: 'subtitle',
          spacing: 10,
        },
        labelDisplayConfig: {
          maxLabels: 40,
          minLabelSpacing: 5,
          labelFontSize: 10,
        },
        formatting: {
          percentage: false,
          sig_figs: 0,
        },
        title: '',
        subtitle: '',
      }
    },
  },
  mounted() {
    this.fetchCampaigns()
  },
  methods: {
    async fetchCampaigns() {
      axios
        .get('api/outreach/campaigns/')
        .then(response => {
          this.campaigns = response.data.results || response.data
        })
        .catch(error => {
          console.error('Failed to fetch campaigns:', error)
          this.toast.error('Failed to load campaigns')
        })
    },
    async fetchReportingData() {
      if (!this.selectedCampaignId) {
        this.reportingData = null
        return
      }

      this.loading = true
      try {
        const response = await axios.get(`api/outreach/campaigns/${this.selectedCampaignId}/reporting/`)
        this.reportingData = response.data
      } catch (error) {
        console.error('Failed to fetch reporting data:', error)
        this.toast.error('Failed to load reporting data')
      } finally {
        this.loading = false
      }
    },
    getLiftBorderColor() {
      if (!this.reportingData?.lift?.percentage) return 'border-gray-300'
      const lift = this.reportingData.lift.percentage
      if (lift > 20) return 'border-green-500'
      if (lift > 5) return 'border-blue-500'
      if (lift > -5) return 'border-gray-400'
      return 'border-red-500'
    },
    getLiftTextColor() {
      if (!this.reportingData?.lift?.percentage) return 'text-gray-500'
      const lift = this.reportingData.lift.percentage
      if (lift > 20) return 'text-green-600'
      if (lift > 5) return 'text-blue-600'
      if (lift > -5) return 'text-gray-600'
      return 'text-red-600'
    },
  },
}
</script>
